import is from '@sindresorhus/is';
import {
    type ChangeEvent,
    type FunctionComponent,
    type KeyboardEvent,
    type PropsWithChildren,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { ANIMATION_DURATION, MAIN_TILE_ID } from '~/ui/domain/tiling-engine/constants';
import { useTilingEngineContext } from '~/ui/domain/tiling-engine/TilingEngineContext';

import {
    type ChatComposerActive,
    ChatComposerActiveContext,
    type ChatComposerCallbacks,
    ChatComposerCallbacksContext,
    ChatComposerConversationContext,
    type ChatComposerInput,
    ChatComposerInputContext,
    useChatComposerActive,
    useChatComposerCallbacks,
    useChatComposerInput,
} from '../chat-composer-contexts';
import { CHAT_TILE_ID } from '../constants';
import { useChatContextRegistry } from '../hooks/use-chat-context-registry';
import { useChatConversation } from '../hooks/use-chat-conversation';
import { useChatTranscription } from '../hooks/use-chat-transcription';
import { useDeferredFlag } from '../hooks/use-deferred-flag';
import { useScrollOnPanelGrow } from '../hooks/use-scroll-on-panel-grow';
import { useChatOpen, useChatStore } from '../stores/chat-open-store';

export {
    useChatComposerActive,
    useChatComposerCallbacks,
    useChatComposerConversation,
    useChatComposerInput,
} from '../chat-composer-contexts';

const SCROLL_AFTER_TRANSITION_MS = ANIMATION_DURATION * 1000 + 50;

const COMPOSER_MOUNT_DELAY_MS = 1000;

// Matches the TilingEngine `gap-m` flex gap (→ `--spacing-m: 0.5rem`).
// Threshold for "a tile has crossed under the sticky chat dock".
const CHAT_TILE_GAP_PX = 8;

type ChatComposerProviderProps = PropsWithChildren<{
    readonly isEnabled?: boolean;
}>;

export const ChatComposerProvider: FunctionComponent<ChatComposerProviderProps> = ({ children, isEnabled = false }) => {
    const isExpanded = useChatOpen();
    const baseOpen = useChatStore((state) => state.open);
    const close = useChatStore((state) => state.close);

    const [draft, setDraft] = useState('');
    const [hoveredTileId, setHoveredTileId] = useState<string | null>(null);
    const [hasComposerAppeared, setHasComposerAppeared] = useState(false);
    const [isChatScrolled, setIsChatScrolled] = useState(false);
    const [isContentUnderChat, setIsContentUnderChat] = useState(false);

    const isComposerReady = useDeferredFlag(COMPOSER_MOUNT_DELAY_MS);

    const markComposerAppeared = useCallback(() => setHasComposerAppeared(true), []);

    const { containerRef, registeredTiles, subscribe } = useTilingEngineContext();

    const handleTranscribedText = useCallback((text: string) => {
        setDraft((previous) => (previous.length > 0 ? `${previous} ${text}` : text));
    }, []);

    const transcription = useChatTranscription(handleTranscribedText);

    const handleDraftChange = useCallback((event: ChangeEvent<HTMLTextAreaElement>): void => {
        setDraft(event.target.value);
    }, []);

    const conversation = useChatConversation();

    const draftRef = useRef(draft);
    draftRef.current = draft;
    const conversationSendRef = useRef(conversation.send);
    conversationSendRef.current = conversation.send;
    const openRef = useRef(baseOpen);
    openRef.current = baseOpen;

    const submit = useCallback((): void => {
        const text = draftRef.current.trim();
        if (text.length === 0) return;
        void conversationSendRef.current({ message: text });
        setDraft('');
        openRef.current();
    }, []);

    const sendMessage = useCallback((text: string): void => {
        const trimmed = text.trim();
        if (trimmed.length === 0) return;
        void conversationSendRef.current({ message: trimmed });
        setDraft('');
        openRef.current();
    }, []);

    const handleKeyDown = useCallback(
        (event: KeyboardEvent<HTMLTextAreaElement>): void => {
            if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                submit();
            }
        },
        [submit],
    );

    const scrollSurfaceRight = useCallback((): void => {
        const container = containerRef.current;

        if (is.null(container)) {
            return;
        }

        container.scrollTo({ behavior: 'smooth', left: container.scrollWidth });
    }, [containerRef]);

    const livePanelIds = useMemo(
        () => registeredTiles.filter((tile) => tile.id !== CHAT_TILE_ID).map((tile) => tile.id),
        [registeredTiles],
    );

    useScrollOnPanelGrow(livePanelIds.length, SCROLL_AFTER_TRANSITION_MS, scrollSurfaceRight);

    useEffect(() => {
        if (!isExpanded) {
            setHoveredTileId(null);
        }
    }, [isExpanded]);

    // Scroll the surface to its rightmost rest after the dock width
    // transition completes. Lives in an effect (not in `open()`) so direct
    // `useChatStore.open()` callers (e.g. RoleReviewPrompt) also trigger
    // the scroll — the chat dock is sticky-on-mount, so without this scroll
    // the layout would be left mid-overflow with content under the chat.
    useEffect(() => {
        if (!isExpanded) {
            return;
        }

        const id = window.setTimeout(scrollSurfaceRight, SCROLL_AFTER_TRANSITION_MS);
        return () => window.clearTimeout(id);
    }, [isExpanded, scrollSurfaceRight]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: registeredTiles re-runs the effect on tile mount/unmount; ResizeObserver only catches scroller size changes, not scrollWidth changes from new siblings.
    useEffect(() => {
        if (!isExpanded) {
            setIsChatScrolled(false);
            setIsContentUnderChat(false);
            return;
        }

        const scroller = containerRef.current;

        if (is.null(scroller)) {
            return;
        }

        const measure = (): void => {
            const max = scroller.scrollWidth - scroller.clientWidth;
            const distanceFromRight = max - scroller.scrollLeft;
            const scrolled = max > 0 && distanceFromRight > 1;
            const underChat = max > 0 && distanceFromRight > CHAT_TILE_GAP_PX + 1;
            setIsChatScrolled((previous) => (previous === scrolled ? previous : scrolled));
            setIsContentUnderChat((previous) => (previous === underChat ? previous : underChat));
        };

        measure();
        scroller.addEventListener('scroll', measure, { passive: true });

        const resizeObserver = new ResizeObserver(measure);
        resizeObserver.observe(scroller);

        window.addEventListener('resize', measure);

        // Re-measure once the engine reports the layout has settled (e.g.
        // Main's width transition completing as the chat dock takes its
        // slot). Without this the no-overflow case never re-fires `measure`
        // after Main shrinks, leaving the flags stuck at their pre-settle
        // values.
        const unsubscribe = subscribe((event) => {
            if (event.type === 'layoutSettled') {
                measure();
            }
        });

        return () => {
            scroller.removeEventListener('scroll', measure);
            resizeObserver.disconnect();
            window.removeEventListener('resize', measure);
            unsubscribe();
        };
    }, [isExpanded, containerRef, registeredTiles, subscribe]);

    const anchorTileId = useMemo(() => {
        const isHoveredAlive =
            hoveredTileId !== null && (hoveredTileId === MAIN_TILE_ID || livePanelIds.includes(hoveredTileId));

        if (isHoveredAlive) {
            return hoveredTileId;
        }

        return livePanelIds[livePanelIds.length - 1] ?? MAIN_TILE_ID;
    }, [hoveredTileId, livePanelIds]);

    const activeTileId = isExpanded ? CHAT_TILE_ID : anchorTileId;

    const { activeChatContext, registerChatContext, unregisterChatContext } = useChatContextRegistry(anchorTileId);

    const callbacks = useMemo<ChatComposerCallbacks>(
        () => ({
            close,
            handleDraftChange,
            handleKeyDown,
            hasComposerAppeared,
            isComposerEnabled: isEnabled,
            isComposerReady,
            markComposerAppeared,
            open: baseOpen,
            registerChatContext,
            sendMessage,
            setHoveredTile: setHoveredTileId,
            submit,
            unregisterChatContext,
        }),
        [
            baseOpen,
            close,
            handleDraftChange,
            handleKeyDown,
            hasComposerAppeared,
            isEnabled,
            isComposerReady,
            markComposerAppeared,
            registerChatContext,
            sendMessage,
            submit,
            unregisterChatContext,
        ],
    );

    const active = useMemo<ChatComposerActive>(
        () => ({
            activeChatContext,
            activeTileId,
            anchorTileId,
            isChatScrolled,
            isContentUnderChat,
            isExpanded,
        }),
        [activeChatContext, activeTileId, anchorTileId, isChatScrolled, isContentUnderChat, isExpanded],
    );

    const input = useMemo<ChatComposerInput>(() => ({ draft, transcription }), [draft, transcription]);

    return (
        <ChatComposerCallbacksContext.Provider value={callbacks}>
            <ChatComposerActiveContext.Provider value={active}>
                <ChatComposerInputContext.Provider value={input}>
                    <ChatComposerConversationContext.Provider value={conversation}>
                        {children}
                    </ChatComposerConversationContext.Provider>
                </ChatComposerInputContext.Provider>
            </ChatComposerActiveContext.Provider>
        </ChatComposerCallbacksContext.Provider>
    );
};

export const useChatComposerContext = (): ChatComposerCallbacks & ChatComposerActive & ChatComposerInput => ({
    ...useChatComposerCallbacks(),
    ...useChatComposerActive(),
    ...useChatComposerInput(),
});
